import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/AbsenceTypes/Add";
import { TableData } from "@/App/AbsenceTypes/Table";
import { getAbsenceTypesData } from "@/Api/absenceTypes";
import { AbsenceTypeProps } from "@/types/absence";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AbsenceUnitLabel } from "@/types/absence";
import { PageHeader } from "@/components/PageHeader";
import { ListChecks } from "lucide-react";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";

export type TableDataProps = AbsenceTypeProps;

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const { hasPermission } = usePermissions();
  const unityFilter = useUnityFilter();
  const extraFilters = {
    unit: unityFilter.appliedExtra.unit || undefined,
    requires_document: unityFilter.appliedExtra.requires_document || undefined,
  };

  useEffect(() => {
    if (!unityFilter.ready) return;
    (async () => {
      setState("loading");
      const { data } = await getAbsenceTypesData(1, unityFilter.param, unityFilter.nameParam, extraFilters);
      setListData(data?.response);
      setState("production");
    })();
  }, [
    unityFilter.ready,
    unityFilter.param,
    unityFilter.nameParam,
    unityFilter.appliedExtra,
  ]);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");
        const { data } = await getAbsenceTypesData(listData?.current_page + 1, unityFilter.param, unityFilter.nameParam, extraFilters);
        setListData(data.response);
        setState("production");
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");
        const { data } = await getAbsenceTypesData(listData?.current_page - 1, unityFilter.param, unityFilter.nameParam, extraFilters);
        setListData(data.response);
        setState("production");
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");
        const { data } = await getAbsenceTypesData(listData?.last_page, unityFilter.param, unityFilter.nameParam, extraFilters);
        setListData(data?.response);
        setState("production");
      }
      return;
    },
  };

  return (
    <Main>
      <If condition={hasPermission("manage-absence-types")}>
        <PageHeader
          icon={ListChecks}
          title="Tipos de Abono"
          description="Gerencie os tipos de abono disponíveis por unidade"
        >
          <AddBtn updateFront={setListData} />
        </PageHeader>
        <div className="flex flex-col gap-3">
          <UnityFilter filter={unityFilter} namePlaceholder="Buscar por nome">
            <div className="space-y-1.5 w-full sm:w-60">
              <Label className="text-sm">Duração</Label>
              <Select
                value={unityFilter.extra.unit || "all"}
                onValueChange={(v) => unityFilter.setExtra("unit", v === "all" ? "" : v)}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder="Todas as durações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as durações</SelectItem>
                  {Object.entries(AbsenceUnitLabel).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 w-full sm:w-60">
              <Label className="text-sm">Documento</Label>
              <Select
                value={unityFilter.extra.requires_document || "all"}
                onValueChange={(v) =>
                  unityFilter.setExtra("requires_document", v === "all" ? "" : v)
                }
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">Exige documento</SelectItem>
                  <SelectItem value="0">Não exige documento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </UnityFilter>
          {state === "loading" ? (
            <CreateLoaderTable cols={5} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};

export default PageList;
